export type SourceType = 'article-journal' | 'book' | 'webpage' | 'chapter';

export type SourceTypeOption = {
  id: SourceType;
  label: string;
};

export const SOURCE_TYPES: SourceTypeOption[] = [
  { id: 'article-journal', label: 'Journal article' },
  { id: 'book', label: 'Book' },
  { id: 'webpage', label: 'Website' },
  { id: 'chapter', label: 'Book chapter' },
];

/** Plan §5: field presence per source type. false = hidden. */
export type FieldPresence = 'required' | 'optional' | false;

export type CslFieldId =
  | 'author'
  | 'editor'
  | 'title'
  | 'container-title'
  | 'volume'
  | 'issue'
  | 'page'
  | 'publisher'
  | 'publisher-place'
  | 'edition'
  | 'issued'
  | 'accessed'
  | 'URL'
  | 'DOI';

const HIDDEN: Record<CslFieldId, FieldPresence> = {
  author: false,
  editor: false,
  title: false,
  'container-title': false,
  volume: false,
  issue: false,
  page: false,
  publisher: false,
  'publisher-place': false,
  edition: false,
  issued: false,
  accessed: false,
  URL: false,
  DOI: false,
};

export const FIELDS_BY_SOURCE: Record<
  SourceType,
  Record<CslFieldId, FieldPresence>
> = {
  'article-journal': {
    ...HIDDEN,
    author: 'required',
    title: 'required',
    'container-title': 'required',
    volume: 'optional',
    issue: 'optional',
    page: 'optional',
    issued: 'required',
    URL: 'optional',
    DOI: 'optional',
  },
  book: {
    ...HIDDEN,
    author: 'required',
    editor: 'optional',
    title: 'required',
    publisher: 'required',
    'publisher-place': 'optional',
    edition: 'optional',
    issued: 'required',
    URL: 'optional',
  },
  webpage: {
    ...HIDDEN,
    author: 'required',
    title: 'required',
    'container-title': 'optional',
    issued: 'optional',
    accessed: 'required',
    URL: 'required',
  },
  chapter: {
    ...HIDDEN,
    author: 'required',
    editor: 'optional',
    title: 'required',
    'container-title': 'required',
    page: 'optional',
    publisher: 'required',
    'publisher-place': 'optional',
    edition: 'optional',
    issued: 'required',
    URL: 'optional',
    DOI: 'optional',
  },
};

export function fieldLabel(field: CslFieldId, sourceType: SourceType): string {
  switch (field) {
    case 'title':
      return sourceType === 'chapter' ? 'Chapter title' : 'Title';
    case 'container-title':
      if (sourceType === 'article-journal') return 'Journal name';
      if (sourceType === 'webpage') return 'Site name';
      return 'Book title';
    case 'publisher-place':
      return 'Place of publication';
    case 'URL':
      return 'URL';
    case 'DOI':
      return 'DOI';
    case 'page':
      return 'Pages';
    case 'issued':
      return 'Publication date';
    case 'accessed':
      return 'Date accessed';
    default:
      return field.charAt(0).toUpperCase() + field.slice(1);
  }
}

export function isVisible(sourceType: SourceType, field: CslFieldId): boolean {
  return FIELDS_BY_SOURCE[sourceType][field] !== false;
}

export function isRequired(sourceType: SourceType, field: CslFieldId): boolean {
  return FIELDS_BY_SOURCE[sourceType][field] === 'required';
}
